import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  LogOut, Inbox, MessageSquare, Trash2, RefreshCw,
  FileText, Clock, CheckCircle, Loader2, Bell,
  Plus, Edit, Eye, EyeOff, PenLine, Mail
} from "lucide-react";
import TipTapEditor from "@/components/TipTapEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-yellow-100 text-yellow-700",
  "in-progress": "bg-purple-100 text-purple-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};
const statusOptions = ["new", "contacted", "in-progress", "completed", "cancelled"];

const emptyPost = { title: "", content: "", excerpt: "", category: "General", image_url: "", published: false };

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [loadingData, setLoadingData] = useState(true);
  const [showNotifs, setShowNotifs] = useState(false);
  const [emailConfig, setEmailConfig] = useState(null);
  const [blogDialogOpen, setBlogDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postForm, setPostForm] = useState(emptyPost);
  const [savingPost, setSavingPost] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, inqRes, ctRes, blogRes, notifRes, emailRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/inquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/contacts`, { withCredentials: true }),
        axios.get(`${API}/admin/blog`, { withCredentials: true }),
        axios.get(`${API}/admin/notifications`, { withCredentials: true }),
        axios.get(`${API}/email-config`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setInquiries(inqRes.data);
      setContacts(ctRes.data);
      setPosts(blogRes.data);
      setNotifications(notifRes.data);
      setEmailConfig(emailRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate("/admin/login");
    } finally {
      setLoadingData(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/admin/login"); return; }
    fetchData();
  }, [user, authLoading, navigate, fetchData]);

  const updateStatus = async (id, status) => {
    try { await axios.patch(`${API}/admin/inquiries/${id}`, { status }, { withCredentials: true }); toast.success("Status updated"); fetchData(); } catch { toast.error("Failed to update"); }
  };
  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try { await axios.delete(`${API}/admin/inquiries/${id}`, { withCredentials: true }); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); }
  };
  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try { await axios.delete(`${API}/admin/contacts/${id}`, { withCredentials: true }); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); }
  };
  const markAllRead = async () => {
    try { await axios.patch(`${API}/admin/notifications/read-all`, {}, { withCredentials: true }); fetchData(); } catch { toast.error("Failed"); }
  };

  const openNewPost = () => { setEditingPost(null); setPostForm(emptyPost); setBlogDialogOpen(true); };
  const openEditPost = (post) => { setEditingPost(post); setPostForm({ title: post.title, content: post.content, excerpt: post.excerpt, category: post.category, image_url: post.image_url, published: post.published }); setBlogDialogOpen(true); };

  const savePost = async () => {
    if (!postForm.title || !postForm.content) { toast.error("Title and content required"); return; }
    setSavingPost(true);
    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blog/${editingPost.id}`, postForm, { withCredentials: true });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/admin/blog`, postForm, { withCredentials: true });
        toast.success("Post created");
      }
      setBlogDialogOpen(false);
      fetchData();
    } catch { toast.error("Failed to save post"); } finally { setSavingPost(false); }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try { await axios.delete(`${API}/admin/blog/${id}`, { withCredentials: true }); toast.success("Post deleted"); fetchData(); } catch { toast.error("Failed"); }
  };

  const togglePublish = async (post) => {
    try { await axios.put(`${API}/admin/blog/${post.id}`, { published: !post.published }, { withCredentials: true }); toast.success(post.published ? "Unpublished" : "Published"); fetchData(); } catch { toast.error("Failed"); }
  };

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  if (authLoading || loadingData) {
    return (<div className="min-h-[80vh] flex items-center justify-center" data-testid="admin-loading"><Loader2 className="w-8 h-8 animate-spin text-[#00A4A9]" /></div>);
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F9F9F8]">
      <div className="bg-[#03045e] text-white px-4 sm:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-lg font-medium" data-testid="admin-title">Admin Dashboard</h1>
            <p className="text-sm text-slate-300">Flarial MediTours</p>
          </div>
          <div className="flex items-center gap-3">
            {emailConfig && (
              <div className={`hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${emailConfig.email_configured ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`} data-testid="email-status">
                <Mail className="w-3 h-3" />
                {emailConfig.email_configured ? 'Email Active' : 'Email Not Configured'}
              </div>
            )}
            <div className="relative">
              <Button variant="ghost" size="sm" onClick={() => setShowNotifs(!showNotifs)} className="text-white hover:bg-white/10 relative" data-testid="notif-bell">
                <Bell className="w-4 h-4" />
                {notifications.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">{notifications.unread_count}</span>
                )}
              </Button>
              {showNotifs && (
                <div className="absolute right-0 top-10 w-80 bg-white rounded-xl border border-slate-200 shadow-lg z-50 max-h-80 overflow-y-auto" data-testid="notif-dropdown">
                  <div className="flex items-center justify-between p-3 border-b border-slate-100">
                    <span className="text-sm font-medium text-[#03045e]">Notifications</span>
                    {notifications.unread_count > 0 && (
                      <Button variant="ghost" size="sm" className="text-xs text-[#00A4A9] h-auto p-1" onClick={markAllRead} data-testid="mark-all-read">Mark all read</Button>
                    )}
                  </div>
                  {notifications.notifications.length === 0 ? (
                    <p className="p-4 text-sm text-slate-400 text-center">No notifications</p>
                  ) : (
                    notifications.notifications.slice(0, 15).map((n) => (
                      <div key={n.id} className={`p-3 border-b border-slate-50 text-sm ${n.read ? "text-slate-500" : "text-[#03045e] bg-blue-50/50"}`}>
                        <p className="text-xs">{n.message}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={fetchData} className="text-white hover:bg-white/10" data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="admin-logout-btn"><LogOut className="w-4 h-4 mr-1.5" /> Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="admin-stats">
            {[
              { label: "Total Inquiries", value: stats.total_inquiries, icon: Inbox, color: "text-[#00A4A9]" },
              { label: "New Inquiries", value: stats.new_inquiries, icon: Clock, color: "text-blue-600" },
              { label: "In Progress", value: stats.in_progress, icon: CheckCircle, color: "text-purple-600" },
              { label: "Messages", value: stats.total_contacts, icon: MessageSquare, color: "text-amber-600" },
              { label: "Blog Posts", value: posts.length, icon: PenLine, color: "text-green-600" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl p-5 border border-slate-200" data-testid={`stat-card-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-500">{s.label}</span>
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                </div>
                <div className="font-outfit text-2xl font-semibold text-[#03045e]">{s.value}</div>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="inquiries">
          <TabsList className="bg-white border border-slate-200 rounded-xl p-1 mb-6" data-testid="admin-tabs">
            <TabsTrigger value="inquiries" className="rounded-lg data-[state=active]:bg-[#00A4A9] data-[state=active]:text-white" data-testid="tab-inquiries">
              <Inbox className="w-4 h-4 mr-1.5" /> Inquiries ({inquiries.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-lg data-[state=active]:bg-[#00A4A9] data-[state=active]:text-white" data-testid="tab-contacts">
              <MessageSquare className="w-4 h-4 mr-1.5" /> Messages ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="blog" className="rounded-lg data-[state=active]:bg-[#00A4A9] data-[state=active]:text-white" data-testid="tab-blog">
              <PenLine className="w-4 h-4 mr-1.5" /> Blog ({posts.length})
            </TabsTrigger>
          </TabsList>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            {inquiries.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="no-inquiries">
                <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No inquiries yet</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="inquiries-list">
                {inquiries.map((inq) => (
                  <div key={inq.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`inquiry-item-${inq.id}`}>
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-outfit font-medium text-[#03045e]">{inq.name}</h3>
                          <Badge className={`text-xs ${statusColors[inq.status] || statusColors.new}`}>{inq.status}</Badge>
                          {inq.report && <Badge variant="outline" className="text-xs"><FileText className="w-3 h-3 mr-1" /> Report</Badge>}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-slate-600 mb-2">
                          <span>Email: {inq.email}</span><span>Phone: {inq.phone}</span><span>Country: {inq.country}</span>
                        </div>
                        <div className="text-sm"><span className="text-slate-500">Treatment: </span><span className="font-medium text-[#00A4A9]">{inq.treatment}</span></div>
                        {inq.message && <p className="text-sm text-slate-500 mt-2 bg-slate-50 rounded-lg p-3">{inq.message}</p>}
                        <div className="text-xs text-slate-400 mt-2">{new Date(inq.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <select value={inq.status} onChange={(e) => updateStatus(inq.id, e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#00A4A9]" data-testid={`status-select-${inq.id}`}>
                          {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8" onClick={() => deleteInquiry(inq.id)} data-testid={`delete-inquiry-${inq.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            {contacts.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="no-contacts">
                <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No messages yet</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="contacts-list">
                {contacts.map((ct) => (
                  <div key={ct.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`contact-item-${ct.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-outfit font-medium text-[#03045e] mb-1">{ct.name}</h3>
                        <div className="text-sm text-slate-600 space-y-0.5"><div>Email: {ct.email}</div>{ct.phone && <div>Phone: {ct.phone}</div>}</div>
                        <p className="text-sm text-slate-500 mt-2 bg-slate-50 rounded-lg p-3">{ct.message}</p>
                        <div className="text-xs text-slate-400 mt-2">{new Date(ct.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                      <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8" onClick={() => deleteContact(ct.id)} data-testid={`delete-contact-${ct.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-outfit text-lg font-medium text-[#03045e]">Blog Posts</h3>
              <Dialog open={blogDialogOpen} onOpenChange={setBlogDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white" onClick={openNewPost} data-testid="new-post-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> New Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-outfit text-[#03045e]">{editingPost ? "Edit Post" : "Create New Post"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-sm text-slate-700 mb-1.5 block">Title *</Label>
                      <Input value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} placeholder="Blog post title" data-testid="post-title-input" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-700 mb-1.5 block">Category</Label>
                      <select value={postForm.category} onChange={(e) => setPostForm({ ...postForm, category: e.target.value })} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid="post-category-select">
                        {["General", "Treatment Guide", "Patient Story", "Travel Tips", "Ayurveda", "Kerala Healthcare", "Cost Guide"].map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-700 mb-1.5 block">Excerpt</Label>
                      <Input value={postForm.excerpt} onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })} placeholder="Short description for blog listing" data-testid="post-excerpt-input" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-700 mb-1.5 block">Cover Image</Label>
                      <div className="flex gap-2">
                        <Input value={postForm.image_url} onChange={(e) => setPostForm({ ...postForm, image_url: e.target.value })} placeholder="Image URL or upload..." className="flex-1" data-testid="post-image-input" />
                        <label className="cursor-pointer">
                          <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp" className="hidden" onChange={async (e) => {
                            const file = e.target.files[0]; if (!file) return;
                            const fd = new FormData(); fd.append("file", file);
                            try { const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true }); setPostForm({ ...postForm, image_url: data.url }); toast.success("Image uploaded"); } catch { toast.error("Upload failed"); }
                          }} data-testid="post-image-upload" />
                          <Button type="button" variant="outline" size="sm" className="h-10 px-3" asChild><span>Upload</span></Button>
                        </label>
                      </div>
                      {postForm.image_url && <img src={postForm.image_url} alt="Preview" className="mt-2 h-24 rounded-lg object-cover" />}
                    </div>
                    <div>
                      <Label className="text-sm text-slate-700 mb-1.5 block">Content *</Label>
                      <TipTapEditor
                        content={postForm.content}
                        onChange={(html) => setPostForm({ ...postForm, content: html })}
                        placeholder="Write your blog post content..."
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="published" checked={postForm.published} onChange={(e) => setPostForm({ ...postForm, published: e.target.checked })} className="rounded" data-testid="post-published-checkbox" />
                      <Label htmlFor="published" className="text-sm">Publish immediately</Label>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button onClick={savePost} disabled={savingPost} className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white flex-1" data-testid="save-post-btn">
                        {savingPost ? "Saving..." : editingPost ? "Update Post" : "Create Post"}
                      </Button>
                      <Button variant="outline" onClick={() => setBlogDialogOpen(false)} className="rounded-full" data-testid="cancel-post-btn">Cancel</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {posts.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="no-posts">
                <PenLine className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No blog posts yet</p>
                <p className="text-slate-400 text-sm mt-1">Create your first post to start your blog</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="posts-list">
                {posts.map((post) => (
                  <div key={post.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`post-item-${post.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-outfit font-medium text-[#03045e]">{post.title}</h3>
                          <Badge className={post.published ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                            {post.published ? "Published" : "Draft"}
                          </Badge>
                          <Badge variant="outline" className="text-xs">{post.category}</Badge>
                        </div>
                        {post.excerpt && <p className="text-sm text-slate-500 mb-2">{post.excerpt}</p>}
                        <div className="text-xs text-slate-400">
                          By {post.author} &middot; {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          {post.slug && <span> &middot; /{post.slug}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-[#00A4A9]" onClick={() => togglePublish(post)} data-testid={`toggle-publish-${post.id}`}>
                          {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-[#00A4A9]" onClick={() => openEditPost(post)} data-testid={`edit-post-${post.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => deletePost(post.id)} data-testid={`delete-post-${post.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
